import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authorizedFetch, scopedKey } from '@/lib/queryClient';
import { useAuthStore } from '@/store/auth';

export interface CreditsStatus {
  plan: 'free' | 'plus';
  allowance: number;
  used: number;
  remaining: number;
  resetAt: string;
  enabled: boolean;
  cancelAtPeriodEnd?: boolean;
  window?: { start: string; end: string };
  isUnlimited?: boolean;
}

/**
 * Helper to check if credits are unlimited.
 * Uses the isUnlimited field from API if available, otherwise infers from allowance >= 9999.
 */
export function isUnlimitedCredits(status: CreditsStatus | null | undefined): boolean {
  if (!status) return false;
  if (status.isUnlimited !== undefined) return status.isUnlimited;
  return status.allowance >= 9999;
}

export function useCredits(pollMs: number = 60_000) {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id || null);

  const query = useQuery<CreditsStatus | null>({
    queryKey: scopedKey([userId, '/api/credits']),
    queryFn: async () => {
      const res = await authorizedFetch('/api/credits');
      if (res.status === 401) return null;
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return (await res.json()) as CreditsStatus;
    },
    refetchInterval: pollMs,
    refetchOnWindowFocus: false,
    staleTime: pollMs,
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: scopedKey([userId, '/api/credits']) });
  }

  return { ...query, invalidate };
}


